import { forwardRef } from 'react';

import { isObject } from '@infinex-private/ts-utils/object';
import type { AlignPhysicalX, Gap, Responsive } from '@infinex/ui-styles';
import { cn, useResponsiveProp } from '@infinex/ui-styles';

import type { ViewProps } from './View';
import { View } from './View';

export type YStackProps = {
  /**
   * The alignment of items, relative to the container.
   * @default 'stretch'
   */
  align?: Responsive<AlignPhysicalX | 'stretch'>;
  /** The gap between the items. */
  gap?: Responsive<Gap>;
} & ViewProps;

/**
 * The `YStack` is a primitive component for composing layouts. It is a thin
 * wrapper around the `div` element, that uses flexbox to create vertical
 * (Y axis) stacks of content.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_flexible_box_layout/Basic_concepts_of_flexbox
 */
export const YStack = forwardRef<HTMLElement, YStackProps>(
  function YStack(props, ref) {
    const { align, gap, className, style, ...viewProps } = props;
    const responsiveProp = useResponsiveProp();
    function isMatch<T>(prop: Responsive<T>, value: T) {
      return isObject(prop) ? responsiveProp(prop) === value : prop === value;
    }

    return (
      <View
        ref={ref}
        className={cn(
          'flex flex-col',
          { 'gap-(--stack-gap)': gap != null },
          {
            'items-start': isMatch(align, 'left'),
            'items-center': isMatch(align, 'center'),
            'items-end': isMatch(align, 'right'),
            'items-stretch': isMatch(align, 'stretch'),
          },
          className
        )}
        style={{
          ...style,
          // @ts-expect-error custom properties are valid
          '--stack-gap': isObject(gap) ? responsiveProp(gap) : gap,
        }}
        {...viewProps}
      />
    );
  }
);
